/**
 * Shared report CSS (mirrors the backend report design) + tiny helpers used by
 * every data-driven report component. Each report file imports REPORT_STYLE and
 * the helpers, then renders its OWN layout/tables from its API's JSON.
 */
import { formatDateTimeSeconds } from '@/utils/datetime';

export const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);
export const num = (v, d = 2) =>
  v === null || v === undefined || v === '' || isNaN(Number(v)) ? '—' : Number(v).toFixed(d);
// Datetime formatter for reports. The report APIs emit UTC; this converts to the
// app display timezone (via datetime.js). Non-datetime/empty values fall back to '—'.
export const dt = (v) => (v === null || v === undefined || v === '' ? '—' : formatDateTimeSeconds(v));

export const sevClass = (sev) => {
  switch ((sev || '').toUpperCase()) {
    case 'CRITICAL': return 'deviation-critical';
    case 'MODERATE': return 'deviation-moderate';
    case 'MINOR': return 'deviation-minor';
    default: return '';
  }
};

// Print the report root in a new window using the shared style.
export const printReport = (rootEl, title) => {
  if (!rootEl) return;
  const win = window.open('', '_blank');
  if (!win) return window.print();
  win.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title || 'Report'}</title>` +
    `${REPORT_STYLE}</head><body>${rootEl.outerHTML}</body></html>`
  );
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 400);
};

export const REPORT_STYLE = `<style>
  /* Reset so the print window has no default body margin fighting @page. */
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  .bmr-report, .bmr-report * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  /* Base font matches the reference report: clean sans-serif, 12px body. The
     page padding is handled by @page margins in print — keep on-screen padding
     modest so preview width == print width (no surprise cropping). */
  .bmr-report {
    font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
    color: #2c3e50; background: #fff;
    padding: 12px;
    font-size: 12px; line-height: 1.35;
  }
  .bmr-report .container { width: 100%; margin: 0; }
  .bmr-report header { text-align: center; margin-bottom: 20px; }
  .bmr-report header h1 { margin: 0; font-size: 22px; color: #1a73e8; }
  .bmr-report header p { margin: 5px 0 0; color: #555; }
  .bmr-report .card { border: 1px solid #d0d7de; border-radius: 6px; padding: 16px;
    margin-bottom: 20px; background:#fff; box-shadow: 0 2px 5px rgba(0,0,0,0.05); }
  .bmr-report .card h2 { margin-top: 0; font-size: 16px; color: #1a73e8;
    border-bottom: 2px solid #e5e9f0; padding-bottom: 8px; }

  /* --- Tables: force them to FIT the page width and never overflow. ---
     table-layout:fixed + width:100% makes columns share the page width instead
     of expanding past it (the cause of right-edge cropping). word-break lets
     long unbroken strings (codes, timestamps) wrap instead of shearing the
     layout (the cause of the "slant"). */
  .bmr-report table {
    width: 100%; max-width: 100%;
    border-collapse: collapse; margin-top: 12px;
    font-size: 11px;
    table-layout: fixed;
  }
  .bmr-report th, .bmr-report td {
    border: 1px solid #d0d7de; padding: 6px 8px; text-align: left;
    vertical-align: top;
    overflow-wrap: break-word; word-break: break-word; white-space: normal;
  }
  .bmr-report th { background-color: #eaf1fb; color: #1a1a1a; text-align: center; font-weight: bold; }
  .bmr-report tr:nth-child(even) { background-color: #f9fbfd; }
  .bmr-report .table_data { text-align: center; }
  .bmr-report .header-class { background-color: #204448 !important; color: #fff !important; }
  .bmr-report .header-class-1 th, .bmr-report tr.header-class-1 th { background-color: #8FB1B5 !important; color: #fff !important; }
  .bmr-report .header-class-1 { background-color: #8FB1B5 !important; color: #fff !important; }
  .bmr-report .status { font-weight: bold; padding: 3px 8px; border-radius: 4px; font-size: 11px; display: inline-block; }
  .bmr-report .completed { background-color: #d1f7d6; color: #1b5e20; }
  .bmr-report .in-progress { background-color: #fff3cd; color: #856404; }
  .bmr-report .pending { background-color: #f0f0f0; color: #555; }
  .bmr-report .rejected { background-color: #f8d7da; color: #721c24; }
  .bmr-report .deviation-critical { background-color: #ffcccc; color: #cc0000; font-weight: bold; }
  .bmr-report .deviation-moderate { background-color: #ffe6cc; color: #cc6600; font-weight: bold; }
  .bmr-report .deviation-minor { background-color: #fff9cc; color: #ccaa00; }
  .bmr-report .section-title { margin-top: 18px; }
  .bmr-report .section-title table { margin-top: 0; }
  .bmr-report .pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: bold; }
  .bmr-report .pill-action { background-color: #e0e8ff; color: #1a3a8a; border: 1px solid #8aa0e0; }
  .bmr-report .pill-dev { background-color: #d5f5e3; color: #1e8449; border: 1px solid #82c99e; }
  .bmr-report .pill-muted { background-color: #fdebd0; color: #9c640c; border: 1px solid #e0bd80; }

  @media print {
    .no-print { display: none !important; }
    html, body, .bmr-report, .bmr-report * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    /* A4 with even margins; the report then fills the printable area exactly. */
    @page { size: A4; margin: 12mm; }
    .bmr-report { padding: 0; width: 100%; }
    /* Don't split a table row across two pages — avoids half-cut rows. */
    .bmr-report tr { page-break-inside: avoid; }
    .bmr-report thead { display: table-header-group; }  /* repeat header each page */
    .bmr-report table { page-break-inside: auto; }
  }
</style>`;
